import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, bookingApi, calendarApi } from '../api';
import { useAuth } from '../AuthContext';
import BookingInfoModal from '../components/BookingInfoModal';
import DetailDrawer from '../components/DetailDrawer';
import { RowMenu } from '../components/SettingGroups';
import {
  IconDots, IconGuest, IconClock, IconPin, IconLink, IconHourglass,
  IconSearch, IconCalendar, IconChevronLeft, IconChevronRight, IconChevronDown,
} from '../components/icons';
import {
  Empty, ErrorNote, Modal, Spinner, dateTh, dayIsoOf, initials, minutesOfDay,
  nowHhmm, shiftDate, shopInstantIso, timeOf, todayIso, useLoader,
} from '../components/common';

const LANE_W = 118;   // sticky therapist column
const GUTTER = 32;    // narrow spacer between the lane column and the timeline
const SLOT_W = 118;   // px per slot column
// Reception still needs to see treatments that finish after the 22:00 booking
// cutoff. This is a display boundary only; availability continues to use the
// branch's real day_ends_at value from the backend.
const BOARD_END_MIN = 23 * 60;

const hhmm = (mins, sep = ':') =>
  `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}${sep}${String(mins % 60).padStart(2, '0')}`;

/** Cards write times with dots — 14.16 - 15.16. */
const cardTime = (iso, tz) => hhmm(minutesOfDay(iso, tz), '.');

export default function CalendarBoard() {
  const { branch } = useAuth();
  const location = useLocation();
  const [date, setDate] = useState(todayIso());
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [turningAway, setTurningAway] = useState(false);
  const [dialog, setDialog] = useState(null);   // 'booking' | 'product' | 'package'
  const [filter, setFilter] = useState('');
  const [availOpen, setAvailOpen] = useState(false);
  const [reserveBusy, setReserveBusy] = useState(false);
  const [reserveError, setReserveError] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const centredFor = useRef(null);

  const board = useLoader(() => calendarApi.board(date), [date]);
  const availability = useLoader(() => calendarApi.availability(), [date]);

  const beginCancellation = async (card) => {
    // A paid booking has a money trail. Cancelling only the booking would leave
    // a paid order behind, so take staff to the existing admin-authorised void
    // flow instead. Unpaid bookings can be cancelled directly from the board.
    if (card.paymentStatus === 'paid') {
      try {
        const result = await api.get(`/api/system/appointments/${card.appointmentId}/detail`);
        const orderId = result.detail?.order_id;
        if (!orderId) throw new Error('ไม่พบออเดอร์ของรายการจองนี้');
        // Open only the void dialog. Keeping `order` in the URL also opened the
        // ordinary detail popup underneath, which resurfaced when void closed.
        navigate(`/history?voidOrder=${orderId}`);
      } catch (error) {
        setReserveError(error);
      }
      return;
    }
    setCancelling(card);
  };

  // BookingBuilder can hand a newly created appointment back to the board and
  // request that its real payment dialog opens immediately.
  useEffect(() => {
    const appointmentId = location.state?.openAppointmentId;
    if (!appointmentId) return;

    setSelected({
      appointmentId,
      autoPay: Boolean(location.state?.autoPay),
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // The time marker and the per-card countdowns move on their own.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const grid = useMemo(() => {
    if (!board.data?.branch) return null;
    const { branch } = board.data;

    const toMin = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const startMin = toMin(branch.dayStartsAt);
    let endMin = toMin(branch.dayEndsAt);
    // day_ends_at of 00:00 means "closes at midnight", not "zero-length day".
    if (endMin <= startMin) endMin = 24 * 60;
    endMin = Math.max(endMin, BOARD_END_MIN);

    const slot = branch.slotMinutes || 30;
    return {
      startMin,
      endMin,
      slot,
      columns: Math.ceil((endMin - startMin) / slot),
      pxPerMin: SLOT_W / slot,
      timezone: branch.timezone,
    };
  }, [board.data]);

  const nowMin = grid ? minutesOfDay(new Date(now).toISOString(), grid.timezone) : 0;
  const showNow = date === todayIso() && grid && nowMin >= grid.startMin && nowMin <= grid.endMin;

  // Open on the current time rather than at the start of the day.
  useEffect(() => {
    if (!showNow || !scrollerRef.current || centredFor.current === date) return;
    centredFor.current = date;
    const x = (nowMin - grid.startMin) * grid.pxPerMin;
    scrollerRef.current.scrollLeft = Math.max(0, x - scrollerRef.current.clientWidth / 3);
  }, [showNow, date, nowMin, grid]);

  const setStatus = async (appointmentId, status) => {
    await calendarApi.setAppointmentStatus(appointmentId, status);
    setSelected(null);
    board.reload();
    availability.reload();
  };

  const openBooking = async () => {
    if (reserveBusy) return;
    setReserveBusy(true);
    setReserveError(null);
    try {
      // Check at click time rather than relying on a count loaded with the
      // board. A web request can arrive while reception has this page open.
      const gate = await bookingApi.onlineGate();
      if (gate.blocked) {
        navigate('/booking-website', {
          state: {
            onlineAllocationWarning: {
              pendingToday: gate.pendingToday,
              message: gate.message,
            },
          },
        });
        return;
      }
      setDialog('booking');
    } catch (error) {
      setReserveError(error);
    } finally {
      setReserveBusy(false);
    }
  };

  if (board.loading) return <Spinner />;
  if (board.error) return <ErrorNote error={board.error} />;

  const { lanes, appointments } = board.data;
  const a = availability.data;

  // Cards with nobody assigned sit in the ยังไม่ระบุหมอ lane at the top.
  const laneRows = [{ therapistId: null, unassigned: true }, ...lanes];
  // The corner search narrows the board without refetching it.
  const q = filter.trim().toLowerCase();
  const matchesFilter = (c) => !q || [
    c.serviceNameEn, c.serviceNameTh, c.guestName, c.bookingCode, c.bedCode, c.customerPhone,
  ].some((v) => v && String(v).toLowerCase().includes(q));

  const cardsFor = (therapistId) =>
    appointments.filter(matchesFilter).filter((c) =>
      therapistId === null
        ? !c.therapistIds || c.therapistIds.length === 0
        : (c.therapistIds || []).includes(therapistId));

  const timelineWidth = grid ? grid.columns * SLOT_W : 0;
  const nowLeft = grid ? (nowMin - grid.startMin) * grid.pxPerMin : 0;
  const canMoveAppointments = ['owner', 'manager'].includes(branch?.roleCode);

  const moveAppointment = async ({ card, sourceTherapistId }, targetTherapistId, startMinutes) => {
    if (!canMoveAppointments || movingId) return;
    const snapped = Math.round(startMinutes / grid.slot) * grid.slot;
    const latestStart = grid.endMin - card.durationMinutes;
    const targetMinutes = Math.max(grid.startMin, Math.min(snapped, latestStart));
    const sourceIds = (card.therapistIds || []).map(Number);
    let therapistIds;

    if (targetTherapistId === null) {
      therapistIds = [];
    } else if (sourceTherapistId === null || sourceIds.length === 0) {
      therapistIds = [Number(targetTherapistId)];
    } else {
      therapistIds = sourceIds.map((id) => (
        id === Number(sourceTherapistId) ? Number(targetTherapistId) : id
      ));
      therapistIds = [...new Set(therapistIds)];
    }

    setMovingId(card.appointmentId);
    setReserveError(null);
    try {
      await calendarApi.updateAppointment(card.appointmentId, {
        startAt: shopInstantIso(date, hhmm(targetMinutes)),
        bedId: card.bedId || null,
        therapistIds,
      });
      board.reload();
      availability.reload();
    } catch (error) {
      setReserveError(error);
    } finally {
      setMovingId(null);
      setDragging(null);
    }
  };

  return (
    <>
      <div className="cal-toolbar">
        <button className="btn-today" onClick={() => setDate(todayIso())}>วันนี้</button>
        <button className="btn-step" onClick={() => setDate(shiftDate(date, -1))}>
          <IconChevronLeft size={18} />
        </button>
        <label className="date-box">
          <span>{dateTh(date)}</span>
          <IconCalendar size={17} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button className="btn-step" onClick={() => setDate(shiftDate(date, 1))}>
          <IconChevronRight size={18} />
        </button>
        <button className="btn-outline" onClick={() => setTurningAway(true)}>ปฏิเสธลูกค้า</button>

        <div className="spacer" />

        <button className="btn-outline" onClick={() => navigate('/buy-package')}>ซื้อแพ็กเกจ</button>
        <button className="btn-outline" onClick={() => navigate('/buy-product')}>ซื้อสินค้า</button>
        <button className="btn-solid" disabled={reserveBusy} onClick={openBooking}>จองเลย</button>
      </div>

      <ErrorNote error={reserveError} />

      {!grid ? (
        <div className="card"><Empty icon="hours" message="ยังไม่ได้ตั้งค่าเวลาทำการของสาขานี้" /></div>
      ) : (
        <div className="board" ref={scrollerRef}>
          <div
            className="board-grid"
            style={{ gridTemplateColumns: `${LANE_W}px ${GUTTER}px repeat(${grid.columns}, ${SLOT_W}px)` }}
          >
            {/* header: search corner, spacer, then the time ruler */}
            <div className="cell-corner">
              <label className="corner-search">
                <IconSearch size={16} />
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="ค้นหา" />
              </label>
            </div>
            <div className="cell-gutter-head" />
            {Array.from({ length: grid.columns }, (_, i) => (
              <div className="cell-time" key={i}>{hhmm(grid.startMin + i * grid.slot)}</div>
            ))}

            {/* the "now" band, carrying the clock pill and the ว่าง summary */}
            <div className="now-band" style={{ gridColumn: `1 / span ${grid.columns + 2}` }}>
              {showNow && (
                <div className="now-cluster" style={{ left: LANE_W + GUTTER + nowLeft }}>
                  <span className="now-pill">{hhmm(nowMin)}</span>
                  {a && (
                    <button
                      className={`avail-pill${availOpen ? ' open' : ''}`}
                      onClick={() => setAvailOpen((v) => !v)}
                    >
                      <span>ว่าง</span>
                      <span className="avail-stat"><i className="dot" />{a.freeTherapists} คน</span>
                      <span className="avail-stat"><i className="dot" />{a.freeBeds} เตียง</span>
                      <IconChevronDown size={15} />
                    </button>
                  )}
                  {availOpen && a && (
                    <div className="avail-panel">
                      <div className="avail-row"><span>หมอนวดเข้างาน</span><strong>{a.onDutyTherapists} คน</strong></div>
                      <div className="avail-row"><span>ว่างขณะนี้</span><strong>{a.freeTherapists} คน</strong></div>
                      <div className="avail-row"><span>เตียงทั้งหมด</span><strong>{a.totalBeds} เตียง</strong></div>
                      <div className="avail-row"><span>เตียงว่าง</span><strong>{a.freeBeds} เตียง</strong></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {laneRows.map((lane) => (
              <Lane
                key={lane.therapistId ?? 'unassigned'}
                lane={lane}
                cards={cardsFor(lane.therapistId)}
                grid={grid}
                columns={grid.columns}
                width={timelineWidth}
                showNow={showNow}
                nowLeft={nowLeft}
                now={now}
                onSelect={setSelected}
                onAction={(c, status) => setStatus(c.appointmentId, status)}
                onEdit={setEditing}
                onCancel={beginCancellation}
                canDrag={canMoveAppointments}
                dragging={dragging}
                movingId={movingId}
                onDragStart={setDragging}
                onDragEnd={() => setDragging(null)}
                onMove={moveAppointment}
              />
            ))}
          </div>
        </div>
      )}

      {selected && (
        <DetailDrawer
          appointmentId={selected.appointmentId}
          autoPay={selected.autoPay}
          onClose={() => setSelected(null)}
          onCancel={(card) => {
            setSelected(null);
            beginCancellation(card);
          }}
          onChanged={() => {
            // Consume the one-shot auto-pay request before reloading the board.
            // The reload can briefly unmount the drawer; retaining autoPay=true
            // would otherwise reopen the cash dialog after a successful payment.
            setSelected((current) => (
              current ? { ...current, autoPay: false } : current
            ));
            board.reload();
          }}
        />
      )}

      {editing && (
        <EditAppointmentModal
          card={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            board.reload();
            availability.reload();
          }}
        />
      )}

      {cancelling && (
        <CancelAppointmentModal
          card={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            board.reload();
            availability.reload();
          }}
        />
      )}

      {turningAway && (
        <TurnAwayModal
          date={date}
          onClose={() => setTurningAway(false)}
          onDone={() => setTurningAway(false)}
        />
      )}

      {dialog === 'booking' && (
        <BookingInfoModal
          date={date}
          onClose={() => setDialog(null)}
          onNext={(info) => navigate('/book', { state: { info } })}
        />
      )}

    </>
  );
}

function Lane({
  lane, cards, grid, columns, width, showNow, nowLeft, now,
  onSelect, onAction, onEdit, onCancel, canDrag, dragging, movingId,
  onDragStart, onDragEnd, onMove,
}) {
  const [dragOver, setDragOver] = useState(false);
  const therapistIsActive = ['working', 'on_break'].includes(lane.status);
  const laneStateClass = lane.unassigned
    ? ' unassigned'
    : therapistIsActive ? '' : ' inactive';

  return (
    <>
      <div className={`cell-lane${laneStateClass}`}>
        {lane.unassigned ? (
          <span className="lane-name">ยังไม่ระบุหมอ</span>
        ) : (
          <>
            <span
              className={`lane-avatar ${therapistIsActive ? 'on-duty' : 'inactive'}`}
              title={therapistIsActive ? 'กำลังเข้างาน' : 'ไม่ได้เข้างาน'}
              aria-label={therapistIsActive ? 'หมอนวดกำลังเข้างาน' : 'หมอนวดยังไม่ได้เข้างาน'}
            >
              {lane.photoUrl ? <img src={lane.photoUrl} alt="" /> : initials(lane)}
            </span>
            <span className="lane-name">
              {lane.nicknameEn || lane.nicknameTh}
              {lane.nicknameEn && lane.nicknameTh ? ` (${lane.nicknameTh})` : ''}
            </span>
          </>
        )}
      </div>

      <div className={`cell-gutter${laneStateClass}`} />

      <div
        className={`cell-row${laneStateClass}${dragOver ? ' drag-over' : ''}`}
        style={{ gridColumn: `3 / span ${columns}`, width }}
        onDragOver={(event) => {
          if (!dragging) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDragOver(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (!dragging) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const minutes = grid.startMin + (event.clientX - bounds.left) / grid.pxPerMin;
          onMove(dragging, lane.therapistId ?? null, minutes);
        }}
      >
        {cards.map((c) => (
          <AppointmentCard
            key={c.appointmentId}
            card={c}
            grid={grid}
            now={now}
            onSelect={onSelect}
            onAction={onAction}
            onEdit={onEdit}
            onCancel={onCancel}
            canDrag={canDrag}
            isMoving={movingId === c.appointmentId}
            sourceTherapistId={lane.therapistId ?? null}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        {showNow && <div className="now-line" style={{ left: nowLeft }} />}
      </div>
    </>
  );
}

function AppointmentCard({
  card, grid, now, onSelect, onAction, onEdit, onCancel,
  canDrag, isMoving, sourceTherapistId, onDragStart, onDragEnd,
}) {
  const dragged = useRef(false);
  const start = minutesOfDay(card.startAt, grid.timezone);
  const left = (start - grid.startMin) * grid.pxPerMin;
  const width = Math.max(card.durationMinutes * grid.pxPerMin - 6, 120);
  const serviceState = {
    pending: { label: 'รอเริ่มบริการ', className: 'pending' },
    in_progress: { label: 'กำลังนวด', className: 'in_progress' },
    completed: { label: 'เสร็จสิ้น', className: 'completed' },
    cancelled: { label: 'ยกเลิก', className: 'cancelled' },
  }[card.status] || { label: card.status, className: 'pending' };
  const paymentState = card.paymentStatus || 'unpaid';
  const paymentLabel = {
    unpaid: 'ยังไม่ชำระ',
    partial: 'ชำระบางส่วน',
    paid: 'ชำระแล้ว',
    refunded: 'คืนเงินแล้ว',
  }[paymentState] || paymentState;
  const wasRefunded = paymentState === 'refunded';
  const canChangeBooking = canDrag && card.status === 'pending' && !wasRefunded;
  const canTakePayment = card.status !== 'cancelled'
    && !['paid', 'refunded'].includes(paymentState);

  // The corner chip counts down to the start, then counts out the remaining
  // treatment time — the two states the real board shows.
  const minsTo = Math.round((new Date(card.startAt).getTime() - now) / 60000);
  const minsLeft = Math.round((new Date(card.endAt).getTime() - now) / 60000);

  let chip = null;
  if (card.status === 'pending' && minsTo > 0 && minsTo <= 120) {
    chip = <span className="appt-chip late"><IconHourglass size={13} />{minsTo}m.</span>;
  } else if (card.status === 'in_progress' && minsLeft > 0) {
    chip = <span className="appt-chip"><IconGuest size={13} />{minsLeft}m.</span>;
  }


  return (
    <div
      className={`appt ${card.status}${wasRefunded ? ' refunded' : ''}${canChangeBooking ? ' draggable' : ''}${isMoving ? ' moving' : ''}`}
      style={{ left, width }}
      draggable={canChangeBooking && !isMoving}
      onDragStart={(event) => {
        dragged.current = true;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(card.appointmentId));
        onDragStart({ card, sourceTherapistId });
      }}
      onDragEnd={() => {
        onDragEnd();
        window.setTimeout(() => { dragged.current = false; }, 0);
      }}
      onClick={() => {
        if (!dragged.current) onSelect(card);
      }}
      title={`${card.serviceNameEn} · ${cardTime(card.startAt, grid.timezone)}${canChangeBooking ? ' · ลากเพื่อย้ายเวลา/หมอนวด' : ''}`}
    >
      <span className="appt-bar" />

      <div className="appt-head">
        <RowMenu
          label="เมนูรายการ"
          trigger={<IconDots size={15} className="appt-dots" />}
          items={[
            { label: 'ดูรายละเอียด', onClick: () => onSelect(card) },
            canChangeBooking && {
              label: 'แก้ไข',
              onClick: () => onEdit(card),
            },
            !wasRefunded && card.status === 'pending' && {
              label: 'เริ่มบริการ',
              onClick: () => onAction(card, 'in_progress'),
            },
            !wasRefunded && card.status === 'in_progress' && {
              label: 'เสร็จสิ้น',
              onClick: () => onAction(card, 'completed'),
            },
            canTakePayment && {
              label: 'ชำระเงิน',
              onClick: () => onSelect(card),
            },
            card.status !== 'cancelled' && !wasRefunded && {
              label: card.paymentStatus === 'paid' ? 'ยกเลิกออเดอร์ / คืนเงิน' : 'ยกเลิกการจอง',
              danger: true,
              onClick: () => onCancel(card),
            },
          ]}
        />
        <span className="appt-title">{card.serviceNameEn} {card.serviceNameTh}</span>
        {chip}
      </div>

      <div className="appt-row">
        <IconGuest size={14} />
        <span className="appt-guest">{card.guestName || 'ลูกค้าไม่ระบุชื่อ'}</span>
        {card.isRequested && <span className="appt-link"><IconLink size={12} /></span>}
        {card.partySize > 1 && <span className="appt-plus">+ {card.partySize - 1}</span>}
      </div>

      <div className="appt-row">
        <IconClock size={14} />
        <span>{cardTime(card.startAt, grid.timezone)} - {cardTime(card.endAt, grid.timezone)}</span>
        <span className="appt-sep">·</span>
        <span>{card.durationMinutes} m.</span>
      </div>

      <div className="appt-row">
        <IconPin size={14} />
        <span>{card.bedCode || '–'}</span>
      </div>

      {/* Status is always visible, matching the original schedule. Operational
          actions remain available from the card's ••• menu and detail drawer. */}
      <div className="appt-foot" aria-label="สถานะบริการและการชำระเงิน">
        <span className={`appt-tag ${serviceState.className}`}>
          {serviceState.label}
        </span>
        {card.status !== 'cancelled' && (
          <span className={`appt-tag pay-${paymentState}`}>
            {paymentLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function EditAppointmentModal({ card, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    date: dayIsoOf(card.startAt),
    time: timeOf(card.startAt),
    bedId: card.bedId ? String(card.bedId) : '',
    therapistIds: (card.therapistIds || []).map(Number),
    note: card.note || '',
    discountAmount: String(card.discountAmount || 0),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const at = shopInstantIso(form.date, form.time);
  const beds = useLoader(
    () => calendarApi.beds(card.serviceVariantId, { at, minutes: card.durationMinutes }),
    [card.serviceVariantId, at, card.durationMinutes],
  );
  const therapists = useLoader(
    () => calendarApi.therapists(at, card.durationMinutes),
    [at, card.durationMinutes],
  );

  const toggleTherapist = (id) => {
    setForm((current) => ({
      ...current,
      therapistIds: current.therapistIds.includes(id)
        ? current.therapistIds.filter((value) => value !== id)
        : [...current.therapistIds, id],
    }));
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await calendarApi.updateAppointment(card.appointmentId, {
        startAt: at,
        bedId: form.bedId ? Number(form.bedId) : null,
        therapistIds: form.therapistIds,
        note: form.note,
        discountAmount: Number(form.discountAmount) || 0,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`แก้ไขการจอง — #${card.bookingCode}`}
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={save} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      <ErrorNote error={error} />
      <div className="grid-2">
        <label className="field">
          <span>วันที่</span>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </label>
        <label className="field">
          <span>เวลา</span>
          <input
            type="time"
            value={form.time}
            onChange={(e) => setForm({ ...form, time: e.target.value })}
          />
        </label>
      </div>

      <label className="field">
        <span>เตียง</span>
        <select
          value={form.bedId}
          onChange={(e) => setForm({ ...form, bedId: e.target.value })}
        >
          <option value="">ไม่ระบุเตียง</option>
          {(beds.data?.beds || [])
            .filter((bed) => (bed.supportsService && bed.isFree) || Number(form.bedId) === bed.id)
            .map((bed) => (
              <option key={bed.id} value={bed.id}>
                {bed.code}{bed.roomCode ? ` · ${bed.roomCode}` : ''}
              </option>
            ))}
        </select>
      </label>

      <div className="field">
        <span>หมอนวด</span>
        <div className="check-list">
          {(therapists.data?.therapists || [])
            .filter((therapist) => therapist.isFree || form.therapistIds.includes(therapist.id))
            .map((therapist) => (
              <label key={therapist.id} className="check-row">
                <input
                  type="checkbox"
                  checked={form.therapistIds.includes(therapist.id)}
                  onChange={() => toggleTherapist(therapist.id)}
                />
                <span>
                  {therapist.nicknameEn || therapist.nicknameTh || therapist.code}
                  {!therapist.onDuty ? ' · ยังไม่เข้างาน' : ''}
                </span>
              </label>
            ))}
          {!therapists.loading && !(therapists.data?.therapists || []).length && (
            <span className="hint">ไม่มีรายชื่อ</span>
          )}
        </div>
      </div>

      <label className="field">
        <span>ส่วนลด</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.discountAmount}
          onChange={(e) => setForm({ ...form, discountAmount: e.target.value })}
        />
      </label>

      <label className="field">
        <span>หมายเหตุ</span>
        <textarea
          value={form.note}
          maxLength={500}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
      </label>
    </Modal>
  );
}

function CancelAppointmentModal({ card, onClose, onCancelled }) {
  const lookups = useLoader(() => calendarApi.lookups(), []);
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reasons = (lookups.data?.cancelReasons || [])
    .filter((reason) => reason.applies_to === 'booking' || !reason.applies_to);

  const cancel = async () => {
    if (!reasonId) return setError('กรุณาเลือกเหตุผลที่ยกเลิก');
    setBusy(true);
    setError(null);
    try {
      await bookingApi.setStatus(card.bookingId, 'cancelled', note || undefined, Number(reasonId));
      onCancelled();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`ยกเลิกการจอง — #${card.bookingCode}`}
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ย้อนกลับ</button>
          <button className="btn btn-danger" onClick={cancel} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'ยกเลิกการจอง'}
          </button>
        </>
      )}
    >
      <ErrorNote error={error} />
      <p>การยกเลิกนี้จะยกเลิกบริการที่ยังไม่เสร็จสิ้นทั้งหมดในรายการจองเดียวกัน</p>
      <label className="field">
        <span>เหตุผล</span>
        <select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
          <option value="">เลือกเหตุผล…</option>
          {reasons.map((reason) => (
            <option key={reason.id} value={reason.id}>
              {reason.name_th || reason.name_en}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>หมายเหตุ</span>
        <textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}

/**
 * ปฏิเสธลูกค้า — logs a walk-in the shop could not take.
 * Fields and required markers taken from the live dialog: date, time,
 * *ช่องทางการจอง, *จำนวนลูกค้า, *เหตุผลที่ปฏิเสธ, ชื่อ, เบอร์โทรศัพท์, หมายเหตุ.
 */
function TurnAwayModal({ date, onClose, onDone }) {
  const lookups = useLoader(() => calendarApi.lookups(), []);
  const [form, setForm] = useState(() => ({
    date: date || todayIso(),
    time: nowHhmm(),
    channel: 'walk_in',
    partySize: '',
    reasonId: '',
    contactName: '',
    contactPhone: '',
    note: '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // ปฏิเสธลูกค้า reasons are the cancel_reason rows scoped to turn_away.
  const reasons = (lookups.data?.cancelReasons || [])
    .filter((r) => r.applies_to === 'turn_away' || !r.applies_to);

  const submit = async () => {
    if (!form.partySize) return setError('กรุณากรอกจำนวนลูกค้า');
    if (!form.reasonId) return setError('กรุณาเลือกเหตุผลที่ปฏิเสธ');

    setBusy(true);
    setError(null);
    try {
      await calendarApi.turnAway({
        occurredAt: shopInstantIso(form.date, form.time),
        channel: form.channel,
        partySize: Number(form.partySize),
        reasonId: Number(form.reasonId),
        contactName: form.contactName || undefined,
        contactPhone: form.contactPhone || undefined,
        note: form.note || undefined,
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="ปฏิเสธลูกค้า"
      onClose={onClose}
      footer={
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      {error && <div className="login-error">{error}</div>}

      <div className="when-row turn-away-when">
        <span className="date-box"><IconCalendar size={17} /> {dateTh(form.date)}</span>
        <span className="date-box time"><IconClock size={17} /> {form.time}</span>
      </div>

      <div className="field">
        <label><span className="req">*</span>ช่องทางการจอง</label>
        <div className="static-field">Walk-in</div>
      </div>

      <div className="field">
        <label><span className="req">*</span>จำนวนลูกค้า</label>
        <input
          type="number"
          min="1"
          value={form.partySize}
          onChange={set('partySize')}
          placeholder="จำนวนลูกค้า"
        />
      </div>

      <div className="field">
        <label><span className="req">*</span>เหตุผลที่ปฏิเสธ</label>
        <select value={form.reasonId} onChange={set('reasonId')}>
          <option value="">เลือกเหตุผล…</option>
          {reasons.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name_th || r.name_en}{r.name_th && r.name_en ? ` (${r.name_en})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="grid-2">
        <div className="field">
          <label>ชื่อ</label>
          <input value={form.contactName} onChange={set('contactName')} placeholder="ชื่อ" />
        </div>
        <div className="field">
          <label>เบอร์โทรศัพท์</label>
          <input value={form.contactPhone} onChange={set('contactPhone')} placeholder="เบอร์โทรศัพท์" />
        </div>
      </div>

      <div className="field">
        <label>หมายเหตุ</label>
        <input value={form.note} onChange={set('note')} placeholder="หมายเหตุ" />
      </div>
    </Modal>
  );
}
